import React, { useEffect, useState } from 'react';
import {
  Container, Typography, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Box, Dialog, DialogTitle, DialogContent, DialogActions, TextField, Select, MenuItem, IconButton, CircularProgress, Alert, SelectChangeEvent, FormControl, InputLabel
} from '@mui/material';
import AddIcon from '@mui/icons-material/PersonAdd';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';

interface User {
  id: number;
  username: string;
  role: string;
}

interface AdminSetupPageProps {
  onBack: () => void;
}

const emptyUser = { username: '', password: '', role: 'user' };

type UserForm = typeof emptyUser;

const AdminSetupPage: React.FC<AdminSetupPageProps> = ({ onBack }) => {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);
  const [editUser, setEditUser] = useState<User | null>(null);
  const [form, setForm] = useState<UserForm>(emptyUser);
  const [formError, setFormError] = useState('');
  const [saving, setSaving] = useState(false);

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetch('/api/users', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setUsers(data);
      } else {
        const data = await res.json();
        if (data.detail) {
          if (Array.isArray(data.detail)) {
            const errorMessages = data.detail.map((err: any) => err.msg).join(', ');
            setError(errorMessages);
          } else {
            setError(data.detail);
          }
        } else {
          setError('Failed to fetch users');
        }
      }
    } catch {
      setError('Network error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  const handleOpen = (user?: User) => {
    setForm(user ? { username: user.username, password: '', role: user.role } : emptyUser);
    setEditUser(user || null);
    setFormError('');
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
    setEditUser(null);
    setForm(emptyUser);
    setFormError('');
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm(f => ({ ...f, [name]: value }));
  };

  const handleSelectChange = (e: SelectChangeEvent) => {
    const { name, value } = e.target;
    setForm(f => {
      const newForm = { ...f, [name as string]: value };
      return newForm;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    setFormError('');
    
    // Validate form data
    if (!form.username.trim()) {
      setFormError('Username is required');
      setSaving(false);
      return;
    }
    
    if (!form.role) {
      setFormError('Role is required');
      setSaving(false);
      return;
    }
    
    if (!editUser && !form.password.trim()) {
      setFormError('Password is required for new users');
      setSaving(false);
      return;
    }
    
    try {
      const method = editUser ? 'PUT' : 'POST';
      const url = editUser ? `/api/users/${editUser.id}` : '/api/users';
      
      // When editing, exclude password field to avoid changing it
      const bodyData = editUser ? { username: form.username, role: form.role } : form;
      const body = JSON.stringify(bodyData);
      
      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body,
      });
      
      if (res.ok) {
        handleClose();
        fetchUsers();
      } else {
        const data = await res.json();
        if (data.detail) {
          if (Array.isArray(data.detail)) {
            const errorMessages = data.detail.map((err: any) => err.msg).join(', ');
            setFormError(errorMessages);
          } else {
            setFormError(data.detail);
          }
        } else {
          setFormError('Failed to save user');
        }
      }
    } catch (error) {
      setFormError('Network error');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (user: User) => {
    if (!window.confirm(`Delete user '${user.username}'?`)) return;
    setSaving(true);
    setError('');
    try {
      const res = await fetch(`/api/users/${user.id}`, {
        method: 'DELETE',
        credentials: 'include',
      });
      if (res.ok) {
        fetchUsers();
      } else {
        setError('Failed to delete user');
      }
    } catch {
      setError('Network error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Container maxWidth={false} sx={{ mt: 6, px: 3 }}>
      <Paper elevation={4} sx={{ p: 4, borderRadius: 3 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" mb={2}>
          <Box display="flex" alignItems="center" gap={2}>
            <Button 
              variant="outlined" 
              startIcon={<ArrowBackIcon />}
              onClick={onBack}
            >
              Back to App
            </Button>
            <Typography variant="h5" fontWeight={700} color="primary">
              User Management
            </Typography>
          </Box>
          <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpen()}>
            Add User
          </Button>
        </Box>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {loading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Username</TableCell>
                  <TableCell>Role</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {users.map(user => (
                  <TableRow key={user.id}>
                    <TableCell>{user.username}</TableCell>
                    <TableCell>{user.role}</TableCell>
                    <TableCell align="right">
                      <IconButton color="primary" onClick={() => handleOpen(user)}><EditIcon /></IconButton>
                      <IconButton color="error" onClick={() => handleDelete(user)}><DeleteIcon /></IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>
      <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
        <DialogTitle>{editUser ? 'Edit User' : 'Add User'}</DialogTitle>
        <DialogContent>
          <TextField
            label="Username"
            name="username"
            value={form.username}
            onChange={handleInputChange}
            fullWidth
            margin="normal"
            disabled={!!editUser}
          />
          {!editUser && (
            <TextField
              label="Password"
              name="password"
              type="password"
              value={form.password}
              onChange={handleInputChange}
              fullWidth
              margin="normal"
            />
          )}
          <FormControl fullWidth sx={{ mt: 2 }}>
            <InputLabel id="role-select-label">Role</InputLabel>
            <Select
              labelId="role-select-label"
              name="role"
              value={form.role}
              onChange={handleSelectChange}
              label="Role"
            >
              <MenuItem value="user">User</MenuItem>
              <MenuItem value="admin">Admin</MenuItem>
            </Select>
          </FormControl>
          {formError && <Alert severity="error" sx={{ mt: 2 }}>{formError}</Alert>}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleClose} disabled={saving}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default AdminSetupPage; 